import "./ReminderPicker.scss";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getReminder } from "../../api/reminders/get";
import { saveReminder, deleteReminder } from "../../api/reminders/save";

const pad = (n) => String(n).padStart(2, "0");
const toDateValue = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeValue = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// A suggested time: 09:00 on the deadline (or today), or the next full hour
// if that has passed
const getDefaultTime = (deadline) => {
  const now = new Date();
  const date = deadline ? new Date(deadline) : now;
  const suggestion = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    9,
    0,
  );
  if (suggestion > now) return suggestion;
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 0, 0, 0);
  return nextHour;
};

/**
 * "Add notification": a personal reminder at a date and time, sent to the
 * user as a notification (in the app and as push).
 * @param {Object} target - { taskId } or { todoListId, listItemId }
 * @param {String} deadline - used to suggest a date
 */
function ReminderPicker({ target, deadline }) {
  const { t, i18n } = useTranslation();
  const [reminder, setReminder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState(null);
  const targetKey = JSON.stringify(target);

  useEffect(() => {
    let cancelled = false;
    getReminder(JSON.parse(targetKey))
      .then((data) => {
        if (!cancelled) setReminder(data.reminder);
      })
      .catch((err) => console.error("Error fetching reminder:", err));
    return () => {
      cancelled = true;
    };
  }, [targetKey]);

  const startEditing = () => {
    const start = reminder
      ? new Date(reminder.remindAt)
      : getDefaultTime(deadline);
    setDate(toDateValue(start));
    setTime(toTimeValue(start));
    setError(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const remindAt = new Date(`${date}T${time}`);
    if (!date || !time || Number.isNaN(remindAt.getTime())) {
      setError(t("reminders.invalidTime"));
      return;
    }
    if (remindAt <= new Date()) {
      setError(t("reminders.mustBeFuture"));
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      const data = await saveReminder(target, remindAt.toISOString());
      setReminder(data.reminder);
      setIsEditing(false);
    } catch (err) {
      console.error("Error saving reminder:", err);
      setError(t("reminders.saveFailed"));
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemove = async () => {
    setIsBusy(true);
    setError(null);
    try {
      await deleteReminder(target);
      setReminder(null);
      setIsEditing(false);
    } catch (err) {
      console.error("Error deleting reminder:", err);
      setError(t("reminders.saveFailed"));
    } finally {
      setIsBusy(false);
    }
  };

  if (isEditing) {
    return (
      <div className="reminder-picker">
        <div className="reminder-picker__label">
          <span className="material-symbols-outlined">notifications</span>
          {t("reminders.title")}
        </div>
        <div className="reminder-picker__inputs">
          <input
            type="date"
            value={date}
            min={toDateValue(new Date())}
            onChange={(e) => setDate(e.target.value)}
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
        {error && <div className="reminder-picker__error">{error}</div>}
        <div className="reminder-picker__actions">
          <button
            className="reminder-picker__secondary"
            onClick={reminder ? handleRemove : () => setIsEditing(false)}
            disabled={isBusy}
          >
            {reminder ? t("reminders.remove") : t("common.cancel")}
          </button>
          <button
            className="reminder-picker__primary"
            onClick={handleSave}
            disabled={isBusy}
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button className="reminder-picker__toggle" onClick={startEditing}>
      <span className="material-symbols-outlined">
        {reminder ? "notifications_active" : "add_alert"}
      </span>
      {reminder
        ? new Date(reminder.remindAt).toLocaleString(i18n.language, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })
        : t("reminders.add")}
    </button>
  );
}

export default ReminderPicker;
